import { PermissionModel } from './permission.model';

import { CompanyModel } from './company.model';
import { EDocumentType } from 'app/shared/enums/document-type.enum';
import { EUserType } from 'app/shared/enums/user-type.enum';
import { EMaritalStatus } from 'app/shared/enums/marital-status.enum';
import { EUserDocumentType } from 'app/shared/enums/user-document-type.enum';
import { Option } from 'app/shared/interfaces/option.interface';

export type UserSocialLink = { title: string; url: string };

export type UserTypeProductRef = { type_product_id: number; title: string };

export type UserDocument = {
    user_document_id?: number;
    doc_type: EUserDocumentType | string;
    doc_number: string;
    observation?: string | null;
};

export class UserModel {
    user_id: number;

    name: string;

    phone: string;

    phone_secondary: string | null;

    photo_url: string | null;

    birth_date: string | Date | null;

    marital_status: EMaritalStatus | string | null;

    sector: string | null;

    manager_user_id: number | null;

    admission_date: string | Date | null;

    termination_date: string | Date | null;

    complement: string | null;

    emergency_contact_name: string | null;

    emergency_contact_phone: string | null;

    emergency_contact_relation: string | null;

    documents: UserDocument[] | null;

    current_role_title: string | null;

    email: string;

    email_secondary: string | null;

    social_links: UserSocialLink[] | null;

    bank_name: string | null;

    bank_agency: string | null;

    bank_account: string | null;

    bank_account_type: string | null;

    bank_pix: string | null;

    bank_holder: string | null;

    password: string;

    street: string;

    number: string;

    neighboorhod: string;

    city: string;

    observation: string;

    description: string;

    state_tax_id: string;

    icms_contributor_indicator: number | null;

    cep: string;

    uf: string;

    type_doc: EDocumentType;

    doc_number: string;

    last_sale_date: Date | null;

    /** Saldo disponível de pontuação (fidelidade), quando a API enviar */
    available_loyalty_points?: number | null;

    sale_comission: number;

    user_wallet_seller: number | null;

    user_wallet_seller_deadline: Date;

    is_network: boolean;

    /** Usuários internos: false = inativo (não loga). Independente de soft-delete. */
    is_active: boolean;

    type_user: EUserType;

    permission: PermissionModel | null;

    company: CompanyModel | null;

    company_id: number | null;

    /** Cliente pai quando type_user = BUYER */
    user_customer_id: number | null;

    /** Fornecedor pai quando type_user = SUPPLIER_CONTACT */
    user_supplier_id: number | null;

    type_products: UserTypeProductRef[] | null;

    loyalty_points_balance: number;

    contact_info_hidden?: boolean;

    deletedAt: Date | null;
    
    constructor(data?: Partial<UserModel>) {
        this.user_id = data?.user_id ?? null;

        this.name = data?.name ?? null;

        this.phone = data?.phone ?? null;

        this.phone_secondary = data?.phone_secondary ?? null;

        this.photo_url = data?.photo_url ?? null;

        this.birth_date = data?.birth_date ?? null;

        this.marital_status = data?.marital_status ?? null;

        this.sector = data?.sector ?? null;

        this.manager_user_id = data?.manager_user_id ?? null;

        this.admission_date = data?.admission_date ?? null;

        this.termination_date = data?.termination_date ?? null;

        this.complement = data?.complement ?? null;

        this.emergency_contact_name = data?.emergency_contact_name ?? null;

        this.emergency_contact_phone = data?.emergency_contact_phone ?? null;

        this.emergency_contact_relation = data?.emergency_contact_relation ?? null;

        this.documents = data?.documents ?? null;

        this.current_role_title = data?.current_role_title ?? null;

        this.email = data?.email ?? null;

        this.email_secondary = data?.email_secondary ?? null;

        this.social_links = data?.social_links ?? null;

        this.bank_name = data?.bank_name ?? null;

        this.bank_agency = data?.bank_agency ?? null;

        this.bank_account = data?.bank_account ?? null;

        this.bank_account_type = data?.bank_account_type ?? null;

        this.bank_pix = data?.bank_pix ?? null;

        this.bank_holder = data?.bank_holder ?? null;

        this.password = data?.password ?? null;

        this.street = data?.street ?? null;

        this.number = data?.number ?? null;

        this.neighboorhod = data?.neighboorhod ?? null;

        this.city = data?.city ?? null;

        this.observation = data?.observation ?? null;

        this.description = data?.description ?? null;

        this.state_tax_id = data?.state_tax_id ?? null;

        this.icms_contributor_indicator = data?.icms_contributor_indicator ?? null;

        this.cep = data?.cep ?? null;

        this.uf = data?.uf ?? null;

        this.type_doc = data?.type_doc ?? null;

        this.doc_number = data?.doc_number ?? null;

        this.last_sale_date = data?.last_sale_date ? new Date(data.last_sale_date) : null;

        this.available_loyalty_points = (data as any)?.available_loyalty_points ?? null;

        this.sale_comission = data?.sale_comission ?? null;

        this.user_wallet_seller = (data as any)?.user_wallet_seller ?? null;

        this.user_wallet_seller_deadline = data?.user_wallet_seller_deadline ?? null;

        this.is_network = data?.is_network ?? null;

        this.is_active = data?.is_active !== false;

        this.type_user = data?.type_user ?? null;

        this.permission = data?.permission ?? null;

        this.company = data?.company ?? null;

        this.company_id = data?.company_id ?? data?.company?.company_id ?? null;

        this.user_customer_id = data?.user_customer_id ?? null;

        this.user_supplier_id = data?.user_supplier_id ?? null;

        this.type_products = data?.type_products ?? null;

        this.loyalty_points_balance =
            data?.loyalty_points_balance !== undefined && data?.loyalty_points_balance !== null
                ? Number(data.loyalty_points_balance)
                : 0;

        this.contact_info_hidden = data?.contact_info_hidden ?? false;
        this.deletedAt = data?.deletedAt ? new Date(data.deletedAt) : null;
    }

    /** Rótulo para selects de filtro (vendedor inativo continua aparecendo no histórico). */
    sellerFilterLabel(): string {
        const base = this.name?.trim() || '';
        const inactive = this.is_active === false || !!this.deletedAt;
        return inactive ? `${base} (inativo)` : base;
    }

    get isAdmin(): boolean {
        return this.type_user === EUserType.ADMIN;
    }

    get canDeleteRecords(): boolean {
        return this.isAdmin || (this.permission?.can_delete_records === true);
    }

    get isSeller(): boolean {
        return this.permission?.is_seller === true;
    }

    /** Lista/detalhe de vendas, OP e financeiro restritos ao próprio vendedor quando scope = own. */
    get viewOnlyOwnSales(): boolean {
        return (
            !this.isAdmin &&
            (this.permission?.sales_visibility_scope === 'own' || this.permission?.view_only_own_sales === true)
        );
    }

    /** UI: ocultar coluna/filtro de vendedor quando escopo é apenas os próprios. */
    get financeRestrictedToSellerRelated(): boolean {
        return this.viewOnlyOwnSales;
    }

    /**
     * Acesso à aba Clientes: admin, permissão direta OU a permissão de escopo restrito
     * (carteira + vendas confirmadas próprias) — neste último caso a API já devolve só os dele.
     */
    get canAccessClients(): boolean {
        return (
            this.isAdmin ||
            this.permission?.can_access_clients === true ||
            this.permission?.view_clients_wallet_or_own_confirmed_sales === true
        );
    }

    get canAccessCarriers(): boolean {
        return this.isAdmin || this.permission?.can_access_carriers === true;
    }

    get canAccessSuppliers(): boolean {
        return this.isAdmin || this.permission?.can_access_suppliers === true;
    }

    /**
     * Retorna se o cliente está em negociação
     * @returns boolean
     */
    isInNegotiation(): boolean {
        if (!this.user_wallet_seller) {
            return false;
        }
        if (!this.user_wallet_seller_deadline) {
            return true;
        }
        return new Date(this.user_wallet_seller_deadline).getTime() > Date.now();
    }

    /**
     * Retorna se o cliente está disponível
     * @returns boolean
     */
    isAvailable(): boolean {
        return !this.isInNegotiation();
    }

    /**
     * Retorna o número de dias restantes para cliente voltar a poder negociar
     * @returns Option<number>
     */
    getDaysLeft(): Option<number> {
        if (this.user_wallet_seller_deadline) {
            const deadline = new Date(this.user_wallet_seller_deadline);
            const today = new Date();
            const diffTime = deadline.getTime() - today.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays;
        }
        return null;
    }
}
